import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/http";

const monthLabel = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 1).toLocaleDateString("en-AU", { month: "long", year: "numeric" }); };
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Delta = ({ now, prev, testid }) => {
  const d = now - prev;
  if (!d) return <span className="text-xs text-muted-foreground" data-testid={testid}>same as last month</span>;
  return <span className={`text-xs font-medium ${d > 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={testid}>{d > 0 ? "▲" : "▼"} {Math.abs(d)} vs last month</span>;
};

export const ClientSourcesCard = () => {
  const [months, setMonths] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    api.get("/reports/new-clients").then(({ data }) => {
      const rows = [...(data.by_month_source || [])];
      const cur = thisMonth();
      if (!rows.some((r) => r.month === cur)) rows.push({ month: cur, total: 0, sources: {} });
      rows.sort((a, b) => a.month.localeCompare(b.month));
      setMonths(rows); setIdx(rows.length - 1);
    }).catch(() => setMonths([]));
  }, []);
  const row = months?.[idx];
  const prev = months?.[idx - 1] || { total: 0, sources: {} };
  const sources = useMemo(() => {
    if (!row) return [];
    const keys = new Set([...Object.keys(row.sources || {}), ...Object.keys(prev.sources || {})]);
    return [...keys].map((k) => ({ source: k, n: row.sources?.[k] || 0, p: prev.sources?.[k] || 0 })).filter((s) => s.n || s.p).sort((a, b) => b.n - a.n || b.p - a.p);
  }, [row, prev]);
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="client-sources">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-heading font-semibold">New clients — where from</h3>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={!months || idx <= 0} onClick={() => setIdx((i) => i - 1)} aria-label="Previous month" data-testid="client-sources-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-xs font-medium tabular-nums min-w-[120px] text-center" data-testid="client-sources-month">{row ? monthLabel(row.month) : "…"}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" disabled={!months || idx >= months.length - 1} onClick={() => setIdx((i) => i + 1)} aria-label="Next month" data-testid="client-sources-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {row && (
        <div className="flex items-baseline gap-2 mb-4">
          <span className="font-heading font-bold text-2xl tabular-nums" data-testid="client-sources-total">{row.total}</span>
          <span className="text-sm text-muted-foreground">new client{row.total === 1 ? "" : "s"}</span>
          {months[idx - 1] && <Delta now={row.total} prev={prev.total} testid="client-sources-total-delta" />}
        </div>
      )}
      <div className="space-y-2.5">
        {sources.map((s) => (
          <div key={s.source} data-testid={`client-source-${s.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="flex justify-between items-baseline text-sm mb-1 gap-2">
              <span className="truncate">{s.source}</span>
              <span className="flex items-baseline gap-2 shrink-0"><span className="text-muted-foreground tabular-nums">{s.n}</span>{months[idx - 1] && <span className="text-[11px] text-muted-foreground tabular-nums">({s.p} last month)</span>}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden relative">
              <div className="h-full bg-primary" style={{ width: `${row.total ? (s.n / row.total) * 100 : 0}%` }} />
              {months[idx - 1] && prev.total > 0 && <div className="absolute top-0 h-full w-0.5 bg-foreground/40" style={{ left: `${Math.min(100, (s.p / prev.total) * 100)}%` }} title={`${Math.round((s.p / prev.total) * 100)}% of last month's new clients`} />}
            </div>
          </div>
        ))}
        {months && row && sources.length === 0 && <div className="text-sm text-muted-foreground">No new clients in {monthLabel(row.month)}.</div>}
        {!months && <div className="text-sm text-muted-foreground">Loading…</div>}
      </div>
      <div className="mt-3 text-right"><Link to="/reports?open=new_clients" className="text-xs text-primary hover:underline" data-testid="client-sources-report-link">Full new-clients report →</Link></div>
    </Card>
  );
};
